import { Injectable } from '@angular/core';
import { InveniasNamespace } from 'datasource-service-lib';
import { EntityControllerService } from 'entity-picker-lib';
import { IContactEmail, IContactPhone, IDataSourceApiService, IEntity, IEntityProfile } from 'interfaces-lib';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { IEntityProfileService } from '../../app-entity-preview/ientityprofileservice';
import { ServiceProviderService } from '../service.provider.service';

@Injectable({ providedIn: 'root' })
export class InveniasEntityProfileService implements IEntityProfileService {
  private apiService: IDataSourceApiService;
  private mappers: { [index: string]: (item) => IEntityProfile | undefined } = {
    people: this.peopleMapper.bind(this),
    companies: this.companiesMapper.bind(this),
    programmes: this.programmesMapper.bind(this),
    assignments: this.assignmentsMapper.bind(this),
  };
  private static ERROR_MAPPER = () => {
    throw new Error('Unexpected profile type.');
  };
  private static CIRCLE_IMAGE_INDEX = 12;
  private static BLURRED_IMAGE_INDEX = 13;

  private entityProfileSelectedSubject = new ReplaySubject<IEntityProfile>();
  entityProfileSelected$ = this.entityProfileSelectedSubject.asObservable();

  constructor(private entityControllerService: EntityControllerService, sps: ServiceProviderService) {
    this.apiService = sps.getDataSourceApiService(InveniasNamespace.name);
    this.entityControllerService.entitySelected$.subscribe((entity) => {
      this.getProfile(entity).subscribe((profile) => {
        this.entityProfileSelectedSubject.next(profile);
      });
    });
    console.debug('InveniasEntityProfileService created');
  }

  getProfile(entity: IEntity): Observable<IEntityProfile> {
    const mapFn = this.mappers[entity.type] || InveniasEntityProfileService.ERROR_MAPPER;
    return this.apiService.get(entity).pipe(map((fatEntity) => mapFn(fatEntity)));
  }

  private peopleMapper(entityData: any): IEntityProfile {
    const primaryPhones: any[] = entityData.DefaultPosition.PhoneNumbers.filter((pn) => pn.FormattedValue); // Business Numbers
    const secondaryPhones: any[] = entityData.PhoneNumbers.filter((pn) => pn.FormattedValue); // inc. Mobile
    const contactPhones: IContactPhone[] = [...primaryPhones, ...secondaryPhones].map((pn) => ({
      displayTitle: pn.DisplayTitle,
      formattedPhone: pn.FormattedValue,
      fieldName: pn.FieldName,
    }));
    const contactEmails: IContactEmail[] = entityData.EmailAddresses.filter((ea) => ea.ItemValue).map((ea) => ({
      emailAddress: ea.ItemValue,
      isBusiness: ea.IsBusiness,
      isPersonal: ea.IsPersonal,
    }));

    return {
      id: entityData.id,
      type: entityData.type,
      name: entityData.NameComponents.FullName,
      contactPhones: contactPhones,
      contactEmails: contactEmails,
      profilePic: entityData.RecordPictures[InveniasEntityProfileService.CIRCLE_IMAGE_INDEX].Uri,
      profilePicBlurred: entityData.RecordPictures[InveniasEntityProfileService.BLURRED_IMAGE_INDEX].Uri,
      jobTitle: entityData.DefaultPosition.JobTitle,
      companyName: entityData.DefaultPosition.Company?.ItemDisplayText,
    };
  }

  private companiesMapper(entityData: any): IEntityProfile {
    return {
      id: entityData.id,
      type: entityData.type,
      name: entityData.CompanyName,
      contactPhones: entityData.DefaultLocation.PhoneNumbers.filter((pn) => pn.FormattedValue).map((pn) => ({
        displayTitle: pn.DisplayTitle,
        formattedPhone: pn.FormattedValue,
        fieldName: pn.FieldName,
      })),
      contactEmails: entityData.DefaultLocation.EmailAddresses.filter((ea) => ea.ItemValue).map((ea) => ({
        emailAddress: ea.ItemValue,
        isBusiness: ea.IsBusiness,
        isPersonal: ea.IsPersonal,
      })),
      profilePic: entityData.RecordPictures[InveniasEntityProfileService.CIRCLE_IMAGE_INDEX].Uri,
      profilePicBlurred: entityData.RecordPictures[InveniasEntityProfileService.BLURRED_IMAGE_INDEX].Uri,
    };
  }

  private programmesMapper(): IEntityProfile {
    return undefined;
  }

  private assignmentsMapper(): IEntityProfile {
    return undefined;
  }
}
